// The Paper 1 practice's state: start screen or guided steps, which step,
// which version of the variant source, where each revision pack is and which
// tiles are turned over, whether the paper overview has been opened, and the
// source booklet's tab per screen.
//
// Resume: `initialProgress` is the learner's newest row per key on this slide.
// Every row here is an upsert (lib/revise/sourcePaper/steps.ts says why), sent
// through `onSave`. With no `onSave` (a preview, or the builder) the state
// lives in memory only.
//
// The question steps, timed mode and the answers' send panel land in later
// builds (KULA-REVISE-IB-PAPER1-PAPER3-PLAN.md §3); their state grows here.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SourcePaperBlock } from '../../../../../../lib/revise/types';
import {
  REVISE_PACKS,
  SOURCE_PAPER_KEYS,
  emptyPack,
  initialVariant,
  packCards,
  revisionTiles,
  sourcePaperSteps,
  stepDone,
  type PackState,
  type RevisePack,
  type SourcePaperStep,
} from '../../../../../../lib/revise/sourcePaper/steps';
import { sourcePaperVariantSource } from '../../../../../../lib/revise/types';

export type SourcePaperPhase = 'start' | 'guided';

export interface UseSourcePaperOptions {
  p: SourcePaperBlock;
  initialProgress?: Record<string, Record<string, unknown>> | null;
  /** An upsert: one row per key, overwritten. */
  onSave?: (key: string, payload: Record<string, unknown>) => void;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** A saved pack row back into state; anything malformed falls back to a fresh pack. */
function readPack(row: unknown, topics: number): PackState {
  if (!isObj(row) || row.cleared === true) return emptyPack();
  const topic = Number.isInteger(row.topic) && (row.topic as number) >= 0 && (row.topic as number) < topics ? (row.topic as number) : 0;
  const open: Record<number, string[]> = {};
  if (isObj(row.open)) {
    for (const [k, v] of Object.entries(row.open)) {
      const i = Number(k);
      if (Number.isInteger(i) && i >= 0 && i < topics && Array.isArray(v)) open[i] = v.filter((x): x is string => typeof x === 'string');
    }
  }
  return { done: row.done === true, topic, open };
}

export function useSourcePaper({ p, initialProgress, onSave }: UseSourcePaperOptions) {
  const steps = useMemo(() => sourcePaperSteps(p), [p]);
  const variants = useMemo(() => sourcePaperVariantSource(p), [p]);

  const [phase, setPhase] = useState<SourcePaperPhase>('start');
  const [stepIdx, setStepIdx] = useState(0);
  const [variant, setVariantState] = useState<string | null>(() => initialVariant(p));
  /** Which pack the revise step has open, or null for the menu. */
  const [revPart, setRevPart] = useState<RevisePack | null>(null);
  const [packs, setPacks] = useState<Record<RevisePack, PackState>>({ content: emptyPack(), skills: emptyPack() });
  const [seenPaper, setSeenPaper] = useState(false);
  /** The booklet's selected tab per screen. */
  const [tabs, setTabs] = useState<Record<string, string>>({});
  const restored = useRef(false);

  // --- Restore ---------------------------------------------------------------------
  useEffect(() => {
    if (restored.current || !initialProgress) return;
    restored.current = true;
    const rows = initialProgress;
    const v = rows[SOURCE_PAPER_KEYS.variant];
    if (variants && isObj(v) && typeof v.variant === 'string' && variants.variants[v.variant]) setVariantState(v.variant);
    setPacks({
      content: readPack(rows[SOURCE_PAPER_KEYS.revise('content')], packCards(p, 'content').length),
      skills: readPack(rows[SOURCE_PAPER_KEYS.revise('skills')], packCards(p, 'skills').length),
    });
    if (isObj(rows[SOURCE_PAPER_KEYS.paper]) && rows[SOURCE_PAPER_KEYS.paper].seen === true) setSeenPaper(true);
    const pos = rows[SOURCE_PAPER_KEYS.position];
    if (isObj(pos) && pos.cleared !== true) {
      if (pos.phase === 'guided') setPhase('guided');
      if (Number.isInteger(pos.step) && (pos.step as number) >= 0 && (pos.step as number) < steps.length) setStepIdx(pos.step as number);
      if (pos.revPart === 'content' || pos.revPart === 'skills') setRevPart(pos.revPart);
      if (isObj(pos.tabs)) setTabs(Object.fromEntries(Object.entries(pos.tabs).filter(([, t]) => typeof t === 'string' && p.sources[t as string])) as Record<string, string>);
    }
  }, [initialProgress, p, steps.length, variants]);

  // --- Saving ----------------------------------------------------------------------------
  // Position is saved from one effect so every way of moving (rail, Next, Back,
  // a tab) writes the same row, and only after a restore has had its chance.
  const savePosition = useRef(false);
  useEffect(() => {
    if (!savePosition.current) {
      savePosition.current = true;
      return;
    }
    onSave?.(SOURCE_PAPER_KEYS.position, { phase, step: stepIdx, revPart, tabs });
  }, [phase, stepIdx, revPart, tabs, onSave]);

  // Read through a ref so two clicks in one task build on each other, and so
  // the save runs once per change (a state updater may run twice).
  const packsRef = useRef(packs);
  packsRef.current = packs;
  const updatePack = useCallback(
    (pack: RevisePack, f: (s: PackState) => PackState) => {
      const next = f(packsRef.current[pack]);
      packsRef.current = { ...packsRef.current, [pack]: next };
      setPacks(packsRef.current);
      onSave?.(SOURCE_PAPER_KEYS.revise(pack), { ...next });
    },
    [onSave],
  );

  // --- Moving ------------------------------------------------------------------------------
  const step: SourcePaperStep | undefined = steps[stepIdx];

  // Opening the paper overview marks it done (spec §6.4).
  useEffect(() => {
    if (phase === 'guided' && step?.id === 'paper' && !seenPaper) {
      setSeenPaper(true);
      onSave?.(SOURCE_PAPER_KEYS.paper, { seen: true });
    }
  }, [phase, step?.id, seenPaper, onSave]);

  const goTo = useCallback(
    (i: number) => {
      if (i < 0 || i >= steps.length) return;
      setStepIdx(i);
      if (steps[i].id !== 'revise') setRevPart(null);
    },
    [steps],
  );
  const goToId = useCallback((id: SourcePaperStep['id']) => goTo(steps.findIndex((s) => s.id === id)), [goTo, steps]);
  const begin = useCallback(() => setPhase('guided'), []);
  const toStart = useCallback(() => setPhase('start'), []);

  const setVariant = useCallback(
    (v: string) => {
      if (!variants?.variants[v]) return;
      setVariantState(v);
      onSave?.(SOURCE_PAPER_KEYS.variant, { variant: v });
    },
    [variants, onSave],
  );

  const setTab = useCallback((screen: string, letter: string) => setTabs((t) => (t[screen] === letter ? t : { ...t, [screen]: letter })), []);

  // --- Revise ---------------------------------------------------------------------------------
  const openPack = useCallback((pack: RevisePack) => {
    setRevPart(pack);
    // A pack opened again starts from its first topic (the reference build does the same).
    updatePack(pack, (s) => ({ ...s, topic: 0 }));
  }, [updatePack]);
  const closePack = useCallback(() => setRevPart(null), []);
  const toTopic = useCallback((pack: RevisePack, topic: number) => updatePack(pack, (s) => ({ ...s, topic })), [updatePack]);
  const toggleTile = useCallback(
    (pack: RevisePack, topic: number, id: string) =>
      updatePack(pack, (s) => {
        const cur = s.open[topic] ?? [];
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        return { ...s, open: { ...s.open, [topic]: next } };
      }),
    [updatePack],
  );
  /** "Reveal all" when anything is still closed, otherwise "Hide all". */
  const toggleAll = useCallback(
    (pack: RevisePack, topic: number) => {
      const card = packCards(p, pack)[topic];
      if (!card) return;
      const ids = revisionTiles(card).map((t) => t.id);
      updatePack(pack, (s) => {
        const allOpen = ids.every((id) => (s.open[topic] ?? []).includes(id));
        return { ...s, open: { ...s.open, [topic]: allOpen ? [] : ids } };
      });
    },
    [p, updatePack],
  );
  const finishPack = useCallback(
    (pack: RevisePack) => {
      updatePack(pack, (s) => ({ ...s, done: true }));
      setRevPart(null);
    },
    [updatePack],
  );

  const isDone = useCallback((s: SourcePaperStep) => stepDone(s, { packs, seenPaper }), [packs, seenPaper]);
  const resuming = phase === 'start' && (stepIdx > 0 || seenPaper || REVISE_PACKS.some((k) => packs[k].done || Object.keys(packs[k].open).length > 0));

  return {
    p,
    steps,
    step,
    stepIdx,
    phase,
    variant,
    variants,
    revPart,
    packs,
    seenPaper,
    tabs,
    resuming,
    begin,
    toStart,
    goTo,
    goToId,
    setVariant,
    setTab,
    openPack,
    closePack,
    toTopic,
    toggleTile,
    toggleAll,
    finishPack,
    isDone,
  };
}

export type SourcePaperApi = ReturnType<typeof useSourcePaper>;
